import './VirtualKeyboard.css';
import 'react-simple-keyboard/build/css/index.css';

import React, { useEffect, useRef, useState } from 'react';
import Keyboard, { type KeyboardLayoutObject } from 'react-simple-keyboard';
import { KeyboardDisplay, KeyboardKey, KeyboardLayout, KeyboardLayoutName } from './KeyboardLayouts';

type ReactKeyboardRef = any; // I don't know what the type is

const KeyboardLayoutsByInputType = {
  // Input type number cannot be supported because we cannot set the cursor position on number inputs
  // Use type "tel" instead
  // number: KeyboardLayout.NUMERIC,

  tel: KeyboardLayout.NUMERIC,
  date: KeyboardLayout.NUMERIC, // Never tested, might not work very well
  week: KeyboardLayout.NUMERIC, // Never tested, might not work very well

  text: KeyboardLayout.TEXT,
  password: KeyboardLayout.TEXT,
  'datetime-local': KeyboardLayout.TEXT, // Never tested, might not work very well
  month: KeyboardLayout.TEXT, // Never tested, might not work very well
  time: KeyboardLayout.TEXT, // Never tested, might not work very well
  email: KeyboardLayout.TEXT,
  search: KeyboardLayout.TEXT,
  url: KeyboardLayout.TEXT,
  textarea: KeyboardLayout.TEXT,
};

export const VirtualKeyboard = (): JSX.Element | null => {
  const [initialInputValue, setInitialInputValue] = useState<string>();
  const [toggleLayoutOnInput, setToggleLayoutOnInput] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [keyboardLayout, setKeyboardLayout] = useState<KeyboardLayoutObject>(KeyboardLayout.TEXT);
  const [keyboardLayoutName, setKeyboardLayoutName] = useState<KeyboardLayoutName>(KeyboardLayoutName.DEFAULT);
  const [focusedTextInput, setFocusedTextInput] = useState<HTMLInputElement>();
  const virtualKeyboardRef = useRef<ReactKeyboardRef>(null);

  // Show / hide keyboard
  useEffect(() => {
    // The keyboard will appear/disappear on mouse down
    // However, if the user is clicking something, like a submit button or the input itself, the mouse up will happen a few milliseconds after the keyboard is shown/hidden
    // The layout might shift, the page might scroll and the user might misclick because of that.
    // To prevent this, we introduce a small delay to allow the user to finish the click they started.
    // The layout will shift after the click.
    const effectDelayMs = 150;

    if (focusedTextInput !== undefined) {
      const showTimeout = setTimeout(() => {
        setIsKeyboardVisible(true);
      }, effectDelayMs);

      return () => { clearTimeout(showTimeout); };
    }

    const hideTimeout = setTimeout(() => {
      virtualKeyboardRef.current?.destroy();
      setIsKeyboardVisible(false);
    }, effectDelayMs);

    return () => { clearTimeout(hideTimeout); };
  }, [focusedTextInput]);

  // Scroll input into view after keyboard appears
  useEffect(() => {
    if (isKeyboardVisible && focusedTextInput !== undefined) {
      focusedTextInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isKeyboardVisible, focusedTextInput]);

  // Attach to focused text input
  useEffect(() => {
    const setFocusedInputIfFocusedElementIsInput = (event: Event): void => {
      const eventTarget = event.target as HTMLInputElement;

      if (eventTarget.type === 'number') {
        console.warn("The VirtualKeyboard cannot support an input of type number. Use type 'tel' instead.", eventTarget);

        eventTarget.type = 'tel';
      }

      const targetKeyboardLayout = KeyboardLayoutsByInputType[eventTarget.type];
      if (targetKeyboardLayout !== undefined) {
        setKeyboardLayout(targetKeyboardLayout);
        setKeyboardLayoutName(KeyboardLayoutName.DEFAULT);
        setFocusedTextInput(eventTarget);
        setInitialInputValue(eventTarget.value);
      }
    };

    document.addEventListener('focus', setFocusedInputIfFocusedElementIsInput, { capture: true });

    return () => {
      document.removeEventListener('focus', setFocusedInputIfFocusedElementIsInput, { capture: true });
    };
  }, []);

  // Detach when the text input loses focus
  useEffect(() => {
    if (focusedTextInput === undefined || initialInputValue === undefined) {
      return;
    }

    const unsetFocusedTextInput = (): void => {
      if (initialInputValue !== focusedTextInput.value) {
        focusedTextInput.dispatchEvent(new Event('change')); // Some jQuery handlers use on("change", handler)
      }

      setFocusedTextInput(undefined);
      setInitialInputValue(undefined);
    };

    focusedTextInput.addEventListener('blur', unsetFocusedTextInput, { capture: true });

    return () => {
      focusedTextInput.removeEventListener('blur', unsetFocusedTextInput, { capture: true });
    };
  }, [focusedTextInput, initialInputValue]);

  // Stay in sync with external changes originating from code or physical keyboard
  useEffect(() => {
    if (focusedTextInput === undefined) {
      return;
    }

    const updateKeyboardInput = (): void => {
      virtualKeyboardRef.current.setInput(focusedTextInput.value);
      virtualKeyboardRef.current.setCaretPosition(focusedTextInput.selectionStart, focusedTextInput.selectionEnd);
    };

    /** Without adding a click event listener, moving the caret by tapping somewhere on your input does not work correctly with inputs in a shadow dom.
     * the caret position needs to be explicitly set on click */
    focusedTextInput.addEventListener('input', updateKeyboardInput, { capture: true });
    focusedTextInput.addEventListener('click', updateKeyboardInput, { capture: true });

    return () => {
      focusedTextInput.removeEventListener('input', updateKeyboardInput, { capture: true });
      focusedTextInput.removeEventListener('click', updateKeyboardInput, { capture: true });
    };
  }, [focusedTextInput]);

  const onInput = (newValue: string): void => {
    if (focusedTextInput === undefined) {
      return;
    }

    if (toggleLayoutOnInput) {
      setKeyboardLayoutName(toggleLayout(keyboardLayoutName));
      setToggleLayoutOnInput(false);
    }

    setInputValue(focusedTextInput, newValue, virtualKeyboardRef.current.caretPosition);
  };

  const onKeyPress = (keyboardKey: string): void => {
    if (focusedTextInput === undefined || virtualKeyboardRef.current === null) {
      return;
    }

    switch (keyboardKey) {
      case KeyboardKey.SHIFT:
        setToggleLayoutOnInput(!toggleLayoutOnInput);
        setKeyboardLayoutName(toggleLayout(keyboardLayoutName));
        break;

      case KeyboardKey.CAPS_LOCK:
        setKeyboardLayoutName(toggleLayout(keyboardLayoutName));
        break;

      case KeyboardKey.SUBMIT:
        focusedTextInput.blur();
        break;

      default: {
        break;
      }
    }
  };

  if (!isKeyboardVisible) {
    return null;
  }

  return (
    <div id="virtual_keyboard">
      <Keyboard
        keyboardRef={(ref) => {
          virtualKeyboardRef.current = ref;
        }}
        onInit={keyboard => {
          if (keyboard === undefined) {
            console.error('The virtual keyboard is open, but not available. This is not supposed to happen.');
            return;
          }

          if (focusedTextInput === undefined) {
            console.error("The virtual keyboard is open, but there's no focusedTextInput. This is not supposed to happen.");
            return;
          }

          const textWasSelectedBeforeInit = isTextSelected(focusedTextInput);
          keyboard.setCaretPosition(focusedTextInput.selectionStart);
          keyboard.setInput(focusedTextInput.value);
          if (textWasSelectedBeforeInit !== undefined && textWasSelectedBeforeInit) {
            focusedTextInput.select();
          }

          keyboard.setCaretPosition(focusedTextInput.selectionStart);
          keyboard.setInput(focusedTextInput.value);
        }}
        onChange={onInput}
        onKeyPress={onKeyPress}
        layout={keyboardLayout}
        layoutName={keyboardLayoutName}
        preventMouseDownDefault={true}
        stopMouseDownPropagation={true}
        display={KeyboardDisplay}
        newLineOnEnter={true}
      />
    </div>
  );
};

/**
 * Sets the input value in a way that can be picked up by React if the element is managed by React.
 *
 * I don't understand any of this, but other people do
 * - https://stackoverflow.com/questions/42550341/react-trigger-onchange-if-input-value-is-changing-by-state/59939017#59939017
 */
function setInputValue (inputElement: HTMLInputElement, inputValue: string, caretPosition: number): void {
  const valueSetter = Object.getOwnPropertyDescriptor(inputElement, 'value')?.set;

  const inputPrototype = Object.getPrototypeOf(inputElement);
  const prototypeValueSetter = Object.getOwnPropertyDescriptor(inputPrototype, 'value')?.set;

  if (valueSetter !== undefined && prototypeValueSetter !== undefined && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(inputElement, inputValue);
  }
  else if (valueSetter !== undefined) {
    valueSetter.call(inputElement, inputValue);
  }
  else {
    // Not managed by React, set it the normal way
    inputElement.value = inputValue;
  }

  // The input caret position doesn't stay in sync after setting the value programmatically, so we set it
  inputElement.setSelectionRange(caretPosition, caretPosition);

  inputElement.dispatchEvent(new Event('input')); // Some jQuery handlers use on("input", handler)
  inputElement.dispatchEvent(new Event('input', { bubbles: true })); // Used by React onChange handlers
}

function toggleLayout (keyboardLayoutName: KeyboardLayoutName): KeyboardLayoutName {
  return keyboardLayoutName === KeyboardLayoutName.DEFAULT
    ? KeyboardLayoutName.SHIFT
    : KeyboardLayoutName.DEFAULT;
}

// checks to see if the text in the input is selected
function isTextSelected (inputElement: HTMLInputElement | HTMLTextAreaElement): boolean | undefined {
  if (typeof inputElement.selectionStart == 'number') {
    return inputElement.selectionStart == 0 && inputElement.selectionEnd == inputElement.value.length;
  }
  else if (typeof window.getSelection != 'undefined') {
    const selection = window.getSelection();
    return selection?.toString() === inputElement.value;
  }
}
